import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import './style.scss';
import { TranslationService } from '../../../services/translation.service';
import ValidationInfo from '../../../validation/validation-info';
import { LanguageService } from '../../../services/language.service';
import { PersonDataApiModel } from './models/person-data.api-model';
import { SanitizerService } from '../../../services/sanitizer.service';
import type IContactInformation from '../../../models/contact-information.model';
import type { IServerValidationError } from '../validatable.interface';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../TextFieldFormGroup';
import type IValidatable from '../validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../helpers/validation.helper';
import ValidationEngine from '../../../validation/validation-engine';
import { Status } from '../../../hooks/fetch.hook';
import { emptyContactInformation } from '../../../models/contact-information.model';
import { ContactInformationLookupField } from './contact-information-lookup-field.enum';

export interface IContactInformationLookupState {
  value: IContactInformation;
  validationInfo: ValidationInfo;
}

export interface IContactInformationLookupProps {
  state: IContactInformationLookupState;
  onChange?: (value: IContactInformation) => void;
  onKeyUp?: (field: ContactInformationLookupField) => void;
}

export default forwardRef<IValidatable, IContactInformationLookupProps>(function ContactInformationLookup({ state, onChange, onKeyUp }: IContactInformationLookupProps, ref) {
  // === STATE ===
  const [enteredPersonalIdentityNumberState, setEnteredPersonalIdentityNumberState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [lookupStatus, setLookupStatus] = useState(Status.notperformed);
  const [lookupErrorCode, setLookupErrorCode] = useState<number | undefined>(undefined);

  // === REFERENCES ===
  const personalIdentityNumberRef = useRef<IValidatable>(null!);
  const phoneNumberRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [{ component: personalIdentityNumberRef.current, validationInfo: enteredPersonalIdentityNumberState.validationInfo, setState: setEnteredPersonalIdentityNumberState }];
  }

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      // Handle validation of the received personal identity number separately, this is not the same as the
      // state of the personal identity number textbox
      const personalIdentityNumberValidationInfo = new ValidationInfo();
      ValidationEngine.for(personalIdentityNumberValidationInfo).required(state.value.personalIdentityNumber).validate();
      return new ValidationInfo('', [], {
        personalIdentityNumber: personalIdentityNumberValidationInfo,
        phoneNumber: phoneNumberRef.current.checkValidity(),
        email: emailRef.current.checkValidity(),
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        personalIdentityNumber: personalIdentityNumberRef.current.handleServerValidationErrors(errors),
        phoneNumber: phoneNumberRef.current.handleServerValidationErrors(errors),
        email: emailRef.current.handleServerValidationErrors(errors),
      });
    },
  }));

  // === EVENT HANDLERS ===
  async function handleGetAddressClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();

    const [isFormValid] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      return;
    }

    setLookupStatus(Status.loading);
    setLookupErrorCode(undefined);
    try {
      const response = await fetch(`/api/persondata/lookup?personalIdentityNumber=${enteredPersonalIdentityNumberState.value}&language=${LanguageService.getCurrentLanguage()}`);
      if (response.ok) {
        const result = PersonDataApiModel.mapToBusinessModel(await response.json());
        setLookupStatus(Status.success);
        onChange?.({ ...state.value, ...result });
      } else {
        setLookupStatus(Status.failed);
        setLookupErrorCode(response.status);
        onChange?.(emptyContactInformation);
        if (response.status === 400) {
          ValidationHelper.handleServerValidationErrors((await response.json()).errors, getValidatableComponents());
        }
      }
    } catch {
      setLookupStatus(Status.failed);
      onChange?.(emptyContactInformation);
    }
  }

  return (
    <div className="contact-information">
      <div className={state.value.valid ? 'mb-4' : ''}>
        <TextFieldFormGroup
          ref={personalIdentityNumberRef}
          inputId="contact-information-personal-identity-number"
          serverValidationName="personalIdentityNumber"
          labelText={TranslationService.get('common.personalidentitynumber')}
          placeholder={TranslationService.get('common.validation.personalidentitynumberplaceholder')}
          inputMode="numeric"
          pattern="[0-9]*"
          required={true}
          type="text"
          ariaDescribedBy={lookupStatus === Status.failed ? 'contact-information-personal-identity-number-lookup-error' : undefined}
          state={enteredPersonalIdentityNumberState}
          onChange={(value) => {
            setEnteredPersonalIdentityNumberState((ps) => ({ ...ps, value }));
          }}
          onKeyUp={() => {
            setEnteredPersonalIdentityNumberState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }));
            setLookupStatus(Status.notperformed);
          }}
        />
        {((lookupStatus === Status.failed && lookupErrorCode !== 400) || state.validationInfo.children.personalIdentityNumber?.isInvalid) && (
          <span id="contact-information-personal-identity-number-lookup-error" role="alert" className="d-block invalid-feedback mb-3">
            {lookupErrorCode === 404 ? (
              <span>{TranslationService.get('common.persondatalookupnotfoundmessage')}</span>
            ) : (
              lookupErrorCode !== 400 && <span>{TranslationService.get('common.persondatalookuperrormessage')}</span>
            )}
            {state.validationInfo.children.personalIdentityNumber?.isInvalid
              ? state.validationInfo.children.personalIdentityNumber?.getErrors().map((error) => <span key={error}>{error}</span>)
              : null}
          </span>
        )}
        <div className="mb-3">
          <button className="btn btn-secondary mb-3 mb-md-0" disabled={enteredPersonalIdentityNumberState.value.length !== 12} onClick={handleGetAddressClick}>
            {TranslationService.get('common.getaddress')}
          </button>
        </div>
        <div className="bg-gray p-3">
          {!state.value.valid && (
            <div
              className="persondata-lookup-information"
              dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('customerserviceforms.nopersonalidentitynumbertext')) }}
            ></div>
          )}
          {state.value.valid && (
            <>
              <div>
                {TranslationService.get('common.spar.information')}
                <br />
                <a href={TranslationService.get('common.spar.link')} target="_blank" rel="noreferrer">
                  {TranslationService.get('common.spar.linktext')}
                </a>
              </div>
              <div className="mt-2">
                <div className="mb-2">{TranslationService.get('common.persondatalookupsucceededmessage')}</div>
                <div>
                  {state.value.firstName} {state.value.lastName}
                </div>
                {state.value.address && (
                  <>
                    <div>{state.value.address.streetAddress}</div>
                    <div>
                      {state.value.address.postalCode} {state.value.address.city}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {state.value.valid && (
        <div className="row">
          <TextFieldFormGroup
            ref={phoneNumberRef}
            inputId="contact-information-phone-number"
            serverValidationName="phoneNumber"
            labelText={TranslationService.get('common.phonenumber')}
            pattern="\+?[0-9]+"
            required={true}
            autocomplete="tel"
            type="tel"
            className="col-sm-6 mb-sm-0"
            state={{ value: state.value.phoneNumber, validationInfo: state.validationInfo.children.phoneNumber ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, phoneNumber: value })}
            onKeyUp={() => onKeyUp?.(ContactInformationLookupField.phoneNumber)}
          />
          <TextFieldFormGroup
            ref={emailRef}
            inputId="contact-information-email"
            serverValidationName="email"
            labelText={TranslationService.get('common.email')}
            required={true}
            autocomplete="email"
            type="email"
            className="col-sm-6 mb-sm-0"
            state={{ value: state.value.email, validationInfo: state.validationInfo.children.email ?? new ValidationInfo() }}
            onChange={(value) => onChange?.({ ...state.value, email: value })}
            onKeyUp={() => onKeyUp?.(ContactInformationLookupField.email)}
          />
        </div>
      )}
    </div>
  );
});
